import React, { useState, useEffect, useMemo } from 'react';
import { getAllOrders, deleteOrder } from '../utils/storage';

// Design Approval utilities
const APPROVAL_STORAGE_KEY = 'blissApprovalRequests';

function getApprovalStatus(orderNumber) {
  try {
    const stored = localStorage.getItem(APPROVAL_STORAGE_KEY);
    if (!stored) return null;
    const approvals = JSON.parse(stored);
    return approvals.find(a => 
      a.orderId === orderNumber || 
      a.orderNumber === orderNumber ||
      (a.orderId && a.orderId.includes(orderNumber))
    ) || null;
  } catch (error) {
    return null;
  }
}

export default function OrderList({ onSelectOrder, onCreateNew }) {
  const [orders, setOrders] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [approvals, setApprovals] = useState({});

  useEffect(() => {
    loadOrders();
  }, []);

  // Load approval statuses for all orders
  useEffect(() => {
    const approvalMap = {};
    orders.forEach(order => {
      approvalMap[order.orderNumber] = getApprovalStatus(order.orderNumber);
    });
    setApprovals(approvalMap);
  }, [orders]);

  const loadOrders = () => {
    const allOrders = getAllOrders();
    setOrders(allOrders);
  };

  const handleDelete = (orderNumber, e) => {
    e.stopPropagation();
    if (window.confirm(`Delete order ${orderNumber}?`)) {
      deleteOrder(orderNumber);
      loadOrders();
    }
  };

  const filteredOrders = orders.filter(order =>
    order.orderNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (order.customerName && order.customerName.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Calculate approval stats
  const approvalStats = useMemo(() => {
    let approved = 0;
    let pending = 0;
    let noApproval = 0;
    
    Object.values(approvals).forEach(a => {
      if (a && a.status === 'approved' && a.signature) {
        approved++;
      } else if (a && a.status === 'pending') {
        pending++;
      } else {
        noApproval++;
      }
    });
    
    return { approved, pending, noApproval };
  }, [approvals]);

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
        <div>
          <h2 className="text-2xl font-light text-white mb-1">All Orders</h2>
          <p className="text-gray-light text-sm">{orders.length} total orders</p>
        </div>
        <button
          onClick={onCreateNew}
          className="btn-gold px-6 py-3 text-sm flex items-center gap-2"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
          New Order
        </button>
      </div>

      {/* Approval Status Summary */}
      {orders.length > 0 && (
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="card p-4 text-center border-green-500/30">
            <div className="text-2xl font-light text-green-400 mb-1">{approvalStats.approved}</div>
            <div className="text-xs uppercase tracking-wider text-gray-custom">Ready for Fabrication</div>
          </div>
          <div className="card p-4 text-center border-yellow-500/30">
            <div className="text-2xl font-light text-yellow-400 mb-1">{approvalStats.pending}</div>
            <div className="text-xs uppercase tracking-wider text-gray-custom">Awaiting Signature</div>
          </div>
          <div className="card p-4 text-center border-red-500/30">
            <div className="text-2xl font-light text-red-400 mb-1">{approvalStats.noApproval}</div>
            <div className="text-xs uppercase tracking-wider text-gray-custom">Need Approval</div>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="card p-6 mb-6">
        <label className="section-label block mb-3">Search Orders</label>
        <input
          type="text"
          placeholder="Search by order number or customer name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="input-styled"
        />
      </div>

      {/* Orders List */}
      <div className="card overflow-hidden">
        {filteredOrders.length === 0 ? (
          <div className="text-center py-16 px-6">
            <svg className="mx-auto mb-4 opacity-30" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="12" y1="8" x2="12" y2="16"></line>
              <line x1="8" y1="12" x2="16" y2="12"></line>
            </svg>
            <p className="text-gray-custom text-lg mb-2">
              {searchTerm ? 'No orders found' : 'No orders yet'}
            </p>
            <p className="text-gray-custom text-sm opacity-70">
              {searchTerm ? 'Try a different search term' : 'Create your first order to get started'}
            </p>
          </div>
        ) : (
          <table className="table-styled">
            <thead>
              <tr>
                <th>Order #</th>
                <th>Customer</th>
                <th>Configuration</th>
                <th>Approval</th>
                <th>Due Date</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.map(order => {
                const approval = approvals[order.orderNumber];
                const isApproved = approval && approval.status === 'approved' && approval.signature;
                const isPending = approval && approval.status === 'pending';
                
                return (
                  <tr
                    key={order.orderNumber}
                    onClick={() => onSelectOrder(order)}
                    className="cursor-pointer"
                  >
                    <td>
                      <span className="text-warm-gold font-semibold">{order.orderNumber}</span>
                    </td>
                    <td>{order.customerName || 'N/A'}</td>
                    <td>
                      <span className="text-gray-light">{order.bedConfig}</span>
                    </td>
                    <td>
                      {isApproved ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-green-500/20 text-green-400">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                            <polyline points="20 6 9 17 4 12"/>
                          </svg>
                          Approved
                        </span>
                      ) : isPending ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-yellow-500/20 text-yellow-400">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"/>
                            <polyline points="12 6 12 12 16 14"/>
                          </svg>
                          Pending
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-red-500/20 text-red-400">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"/>
                            <line x1="15" y1="9" x2="9" y2="15"/>
                            <line x1="9" y1="9" x2="15" y2="15"/>
                          </svg>
                          Required
                        </span>
                      )}
                    </td>
                    <td>
                      <span className="text-gray-light">{order.dueDate || 'N/A'}</span>
                    </td>
                    <td className="text-right">
                      <button
                        onClick={(e) => handleDelete(order.orderNumber, e)}
                        className="px-3 py-1.5 text-xs font-medium text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded transition"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
